import React, { useState } from 'react';
import { Link } from 'react-router-dom';

const styles = {
  container: {
    backgroundColor: 'transparent', // Let App.js dark background shine through
    minHeight: '100vh',
    padding: '80px 20px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    fontFamily: '"Inter", sans-serif',
    color: '#f8fafc', // Light text
  },
  header: {
    textAlign: 'center',
    marginBottom: '60px',
  },
  title: {
    fontSize: '3rem',
    fontWeight: '900',
    color: '#ffffff', // Pure white
    marginBottom: '10px',
    letterSpacing: '-1px',
  },
  subtitle: {
    fontSize: '1.2rem',
    color: '#94a3b8', // Slate grey
    maxWidth: '600px',
    margin: '0 auto',
    lineHeight: '1.6',
  },
  faqContainer: {
    width: '100%',
    maxWidth: '800px', // focused width for reading
    display: 'flex',
    flexDirection: 'column',
    gap: '20px',
  },
  // ACCORDION ITEM
  item: {
    border: '1px solid #1c2b4d', // Subtle dark blue border
    borderRadius: '12px',
    backgroundColor: '#111c3a', // Dark navy card
    overflow: 'hidden', 
    transition: 'box-shadow 0.2s, transform 0.2s',
  },
  itemOpen: {
    boxShadow: '0 10px 30px rgba(0,0,0,0.4)', // Darker shadow
    border: '1px solid #b026ff', // Signature purple border when open!
  },
  questionBtn: {
    width: '100%',
    padding: '25px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    textAlign: 'left',
  },
  questionText: {
    fontSize: '1.2rem',
    fontWeight: 'bold',
    color: '#f8fafc', // Off-white
  },
  icon: {
    fontSize: '1.5rem',
    color: '#b026ff', // Violet accent
    transition: 'transform 0.3s',
  },
  answerContainer: {
    padding: '0 25px 25px 25px',
    color: '#cbd5e1', // Light slate text for readability
    lineHeight: '1.7',
    fontSize: '1.05rem',
    borderTop: '1px solid #1c2b4d', // Subtle separator matching the border
    marginTop: '-10px', 
    paddingTop: '20px',
  },
  link: {
    color: '#b026ff', // Purple links
    fontWeight: 'bold',
    textDecoration: 'none',
  }
};

// FAQ DATA ARRAY - Easy to add more later!
const faqData = [
  {
    question: "How are you using AI?",
    answer: (
      <>
        Some of our games, like <Link to="/games/lonelyhands" style={styles.link}>LonelyHands</Link>, we do not use AI whatsoever, if our budget permits. For others, which are much larger, we do. For instance, with <Link to="/games/3xr" style={styles.link}>3XRay</Link>, we use AI primarily for our in-game illustrations and art assets, as the sheer amount of artwork required to make our vision come true would be, frankly, way out of our budget. Everything else, such as music, voice acting, and writing, is done by human beings, in all games we produce. Please check our <Link to="/ai-policy" style={styles.link}>Stance on AI</Link> page for more information.
      </>
    )
  },
  {
    question: "Are your games uncensored?",
    answer: "Yes. All our games are designed for an adult audience (18+). While we may offer 'Streamer Mode' toggles in the future, the base experience is fully uncensored."
  },
  {
    question: "What platforms will be supported?",
    answer: "We are currently prioritizing Windows (PC). Macs build are planned for later in development if things go well. An Android version is being considered but is not currently in active production."
  },
  {
    question: "What is your monetization like?",
    answer: "3XRay will have a free public demo that covers the first chapter. The full game will be a paid release. Supporters on Patreon get access to the latest builds as they are developed, once we launch our patreon that is. Other games, like LonelyHands, will be paid once released, with an 'SFW' version considered for free."
  },
  {
    question: "How often do you update?",
    answer: "We aim for constant content updates every week or so, but for major updates, 4-6 weeks is more accurate. You can track our progress on the Devlog page."
  }
];

export default function FAQPage() {
  // State to track which question is open. Null means all closed.
  const [openIndex, setOpenIndex] = useState(null);

  const toggleItem = (index) => {
    // If clicking the already open one, close it. Otherwise open the new one.
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <div style={styles.container}>
      
      <div style={styles.header}>
        <h1 style={styles.title}>Frequently Asked Questions</h1>
        <p style={styles.subtitle}>
          Have a question about development, platforms, or our games? 
          Check below. If you can't find an answer, feel free to email us at contact@foreplaygames.dev or message us on X!
        </p>
      </div>

      <div style={styles.faqContainer}>
        {faqData.map((item, index) => {
          const isOpen = openIndex === index;
          
          return (
            <div 
              key={index} 
              style={{
                ...styles.item,
                ...(isOpen ? styles.itemOpen : {})
              }}
            >
              <button 
                style={styles.questionBtn} 
                onClick={() => toggleItem(index)}
              >
                <span style={styles.questionText}>{item.question}</span>
                <span style={{
                  ...styles.icon,
                  transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)'
                }}>
                  ▼
                </span>
              </button>
              
              {isOpen && (
                <div style={styles.answerContainer}>
                  {item.answer}
                </div>
              )}
            </div>
          );
        })}
      </div>

    </div>
  );
}